import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import Header from './components/Header';
import UploadScreen from './components/UploadScreen';
import ProcessingScreen from './components/ProcessingScreen';
import ResultsScreen from './components/ResultsScreen';
import SettingsModal from './components/SettingsModal';
import { AppState, ScoringWeights } from './types';
import { processMembersData, parseExcelData } from './utils/dataProcessor';

const App: React.FC = () => {
  const [appState, setAppState] = useState<AppState>({
    currentScreen: 'upload',
    members: [],
    originalMembers: [],
    weights: { attendance: 0.3, experience: 0.2, contributionGap: 0.5, minCurrentScore: 80 },
    isLoading: false,
    error: null
  });

  const [showSettings, setShowSettings] = useState(false);
  const [currentFileName, setCurrentFileName] = useState<string>('');

  const handleFileSelect = useCallback(async (file: File) => {
    setCurrentFileName(file.name);
    setAppState(prev => ({ ...prev, isLoading: true, error: null, currentScreen: 'processing' }));

    try {
      // Read the Excel file
      const data = await readExcelFile(file);
      
      // Parse the data
      const members = parseExcelData(data);
      
      if (members.length === 0) {
        throw new Error('No valid member data found in the file');
      }

      // Process the members with current weights
      const processedMembers = processMembersData(members, appState.weights);

      setAppState(prev => ({
        ...prev,
        members: processedMembers,
        originalMembers: members,
        isLoading: false,
        currentScreen: 'results'
      }));

    } catch (error) {
      console.error('Error processing file:', error);
      setAppState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'An error occurred while processing the file',
        currentScreen: 'upload'
      }));
    }
  }, [appState.weights]);

  const readExcelFile = (file: File): Promise<any[][]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          resolve(jsonData as any[][]);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsBinaryString(file);
    });
  };

  const handleWeightsChange = useCallback((newWeights: ScoringWeights) => {
    setAppState(prev => ({ ...prev, weights: newWeights }));
    
    // Reprocess with new weights if we have data
    if (appState.originalMembers.length > 0) {
      const processedMembers = processMembersData(appState.originalMembers, newWeights);
      setAppState(prev => ({ ...prev, members: processedMembers }));
    }
  }, [appState.originalMembers]);

  const handleBackToUpload = useCallback(() => {
    setAppState(prev => ({
      ...prev,
      currentScreen: 'upload',
      members: [],
      originalMembers: [],
      error: null
    }));
    setCurrentFileName('');
  }, []);

  const handleReprocess = useCallback(() => {
    if (appState.originalMembers.length > 0) {
      setAppState(prev => ({ ...prev, isLoading: true, currentScreen: 'processing' }));
      
      // Simulate processing delay for better UX
      setTimeout(() => {
        const processedMembers = processMembersData(appState.originalMembers, appState.weights);
        setAppState(prev => ({
          ...prev,
          members: processedMembers,
          isLoading: false,
          currentScreen: 'results'
        }));
      }, 1500);
    }
  }, [appState.originalMembers, appState.weights]);

  const handleExport = useCallback(() => {
    console.log('Export functionality triggered');
    // Export is handled within the DataTable component
  }, []);

  const renderCurrentScreen = () => {
    switch (appState.currentScreen) {
      case 'upload':
        return (
          <UploadScreen 
            onFileSelect={handleFileSelect}
            isLoading={appState.isLoading}
          />
        );
      
      case 'processing':
        return (
          <ProcessingScreen 
            fileName={currentFileName}
          />
        );
      
      case 'results':
        return (
          <ResultsScreen
            members={appState.members}
            originalMembers={appState.originalMembers}
            onBack={handleBackToUpload}
            onReprocess={handleReprocess}
            onExport={handleExport}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      <Header onSettingsClick={() => setShowSettings(true)} />
      
      {appState.error && (
        <div className="bg-accent-50 border-l-4 border-accent-400 p-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-accent-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-accent-700">
                  <strong>Error:</strong> {appState.error}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    onClick={() => setAppState(prev => ({ ...prev, error: null }))}
                    className="inline-flex bg-accent-50 rounded-md p-1.5 text-accent-500 hover:bg-accent-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-accent-50 focus:ring-accent-600"
                  >
                    <span className="sr-only">Dismiss</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {renderCurrentScreen()}
      
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        weights={appState.weights}
        onWeightsChange={handleWeightsChange}
      />
    </div>
  );
};

export default App;
